import React, { FC, useState } from "react";
import { darken, Link } from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import OverflowTooltip from "../../../../components/OverflowTooltip/OverflowTooltip";
import { ReactComponent as PhoneNumber } from "../../../../assets/svg/phone-number.svg";
import { ReactComponent as Website } from "../../../../assets/svg/website.svg";
import { ReactComponent as Email } from "../../../../assets/svg/email.svg";
import { urlNormalizer } from "../../../../helpers";
import useServicesQueries from "../../hooks/useServicesQueries";
import { IViewMoreContactsBackEndModel } from "../../types";
import Button from "../../../../components/Button";
import FontIcon from "../../../../components/FontIcon";
import { useAuth } from "../../../auth/hooks/useAuth";
import { UserRole } from "../../../user/enums/UserRole";
import { urlReplacer } from "../../../../utils";
import { Routes } from "../../../../constants/routes";
import { useHistory } from "react-router-dom";
import { useWizard } from "../../../wizard/context";
import ChooseStudentForm from "../../modals/chooseStudentForMessage/Content";
import { validator } from "../../modals/chooseStudentForMessage/validator";
import theme from "../../../../themes";

const useStyles = makeStyles(() => ({
  header: {
    padding: "24px 32px",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    minHeight: "198px",
  },
  serviceProviderInfoMainContainer: {
    display: "flex",
  },
  serviceProviderImageContainer: {
    margin: "auto 0px",
  },
  serviceProviderImage: {
    backgroundSize: "100% auto",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
  },
  serviceProviderInfoContainer: {
    marginLeft: 32,
    maxWidth: 714,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-evenly",
  },
  serviceProviderName: {
    fontSize: 25,
    fontWeight: "bold",
    color: "#231F20",
    marginTop: 7,
    lineHeight: 1.1,
  },
  sendMessageButton: {
    height: "28px",
    width: "189px",
    padding: "8px 16px !important",
  },
  messageIcon: {
    fontSize: 14,
    marginRight: 8,
  },
  contactsContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  contactIconContainer: {
    display: "flex",
    alignItems: "center",
    "& + &": {
      marginTop: 12,
    },
  },
  title: {
    fontSize: 16,
    color: "#231F20",
    fontWeight: "bold",
    marginLeft: 8,
    maxWidth: 200,
  },
  link: {
    "&:hover": {
      color: "#858997",
      cursor: "pointer",
    },
  },
  hyperlink: {
    fontWeight: "bold",
    color: theme.palette.info.main,
    "&:hover": {
      color: darken(theme.palette.info.main, 0.2),
      cursor: "pointer",
    },
    textDecoration: "none",
  },
}));

interface ServiceProviderInfoWidgetProps {
  serviceId: string;
  serviceProviderId: string;
  name: string;
  image: string;
}

export const ServiceProviderInfoWidget: FC<ServiceProviderInfoWidgetProps> = ({
  serviceId,
  serviceProviderId,
  image,
  name,
}) => {
  const {
    user,
    data: { isAuthenticated, authorizing },
  } = useAuth();
  const classes = useStyles();
  const [contacts, setContacts] = useState<IViewMoreContactsBackEndModel>();
  const { fetchContacts } = useServicesQueries();
  const history = useHistory();
  const { openWizard } = useWizard();
  const { getStudents } = useServicesQueries();

  const isServiceProvider = user?.role === UserRole.ServiceProvider;

  const showContacts = async () => {
    if (!authorizing && !isAuthenticated) {
      openWizard({
        steps: [
          {
            titleConfig: {
              titleText: "Oops",
            },
            content: () => (
              <p>
                Sorry, this feature is only available to logged-in users. Please{" "}
                <Link className={classes.hyperlink} href={`/${Routes.LOGIN}`}>
                  log in
                </Link>{" "}
                to see contacts.
              </p>
            ),
          },
        ],
        submitButtonText: null,
        cancelButtonText: "Close",
      });
      return;
    }
    const data = await fetchContacts(serviceId);

    setContacts(data.data);
  };

  const sendMessage = async () => {
    if (user?.role === UserRole.Student) {
      history.push(
        urlReplacer(`/0/${Routes.USER_MESSAGES}`, {
          userRole: UserRole.ServiceProvider,
          recipientId: serviceProviderId,
        }),
      );
    } else if ([UserRole.Agent, UserRole.Parent].includes(user?.role)) {
      const { data: students } = await getStudents(user.id);
      const studentId = await openWizard({
        steps: [
          {
            width: "500px",
            titleConfig: {
              titleText: "Choose a Student",
            },
            content: ChooseStudentForm,
            validator: validator,
            stepName: "chooseStudent",
          },
        ],
        submitButtonText: "Go to Messages",
        getInitialData: async () => ({ injectedData: { students } }),
        saveCallback: async (data) => ({
          data: data?.chooseStudent?.student?.id,
        }),
        showSuccessNotification: false,
      });

      if (!studentId) return;

      history.push(
        urlReplacer(
          `/0/${Routes.STUDENT}/${Routes.MESSAGES}?userRole=${UserRole.ServiceProvider}&recipientId=${serviceProviderId}`,
          {
            id: studentId,
          },
        ),
      );
    } else if (!!user) {
      openWizard({
        steps: [
          {
            titleConfig: {
              titleText: "Oops",
            },
            content:
              "Apologies, but this functionality isn't accessible yet for users with your account type. If you require further assistance, please get in touch with our support team.",
          },
        ],
        submitButtonText: null,
        cancelButtonText: "Close",
      });
    } else {
      openWizard({
        steps: [
          {
            titleConfig: {
              titleText: "Oops",
            },
            content: () => (
              <p>
                Sorry, this feature is only available to logged-in users. Please{" "}
                <Link className={classes.hyperlink} href={`/${Routes.LOGIN}`}>
                  log in
                </Link>{" "}
                to write a message.
              </p>
            ),
          },
        ],
        submitButtonText: null,
        cancelButtonText: "Close",
      });
    }
  };

  const makeServicePayment = async () => {
    if (
      [UserRole.Agent, UserRole.Student, UserRole.Parent].includes(user?.role)
    ) {
      history.push(
        urlReplacer(`/${Routes.MAKE_SERVICE_PAYMENT}?serviceId=${serviceId}`, {
          serviceId,
        }),
      );
    } else if (!!user) {
      openWizard({
        steps: [
          {
            titleConfig: {
              titleText: "Oops",
            },
            content:
              "Apologies, but this functionality isn't accessible yet for users with your account type. If you require further assistance, please get in touch with our support team.",
          },
        ],
        submitButtonText: null,
        cancelButtonText: "Close",
      });
    } else {
      openWizard({
        steps: [
          {
            titleConfig: {
              titleText: "Oops",
            },
            content: () => (
              <p>
                Sorry, this feature is only available to logged-in users. Please{" "}
                <Link className={classes.hyperlink} href={`/${Routes.LOGIN}`}>
                  log in
                </Link>{" "}
                to make a payment.
              </p>
            ),
          },
        ],
        submitButtonText: null,
        cancelButtonText: "Close",
      });
    }
  };

  return (
    <div className={classes.header}>
      <div className={classes.serviceProviderInfoMainContainer}>
        <div className={classes.serviceProviderImageContainer}>
          <img
            alt={"Service Provider Logo"}
            src={image}
            className={classes.serviceProviderImage}
            width={74}
            height={74}
          />
        </div>
        <div className={classes.serviceProviderInfoContainer}>
          <div className={classes.serviceProviderName}>
            <OverflowTooltip text={name} />
          </div>
          {!isServiceProvider && (
            <>
              <Button
                className={classes.sendMessageButton}
                type={"submit"}
                variant={"contained"}
                color={"secondary"}
                fullWidth
                onClick={sendMessage}
              >
                <FontIcon className={classes.messageIcon} name={"message"} />
                Send a message
              </Button>
              <Button
                className={classes.sendMessageButton}
                type={"submit"}
                variant={"contained"}
                color={"success"}
                fullWidth
                onClick={makeServicePayment}
              >
                <FontIcon className={classes.messageIcon} name={"card"} />
                Make a payment
              </Button>
            </>
          )}
        </div>
      </div>
      <div className={classes.contactsContainer}>
        {!contacts ? (
          <div className={classes.contactIconContainer}>
            <PhoneNumber />
            <div className={classes.title}>
              <div className={classes.link} onClick={showContacts}>
                Show Contacts
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className={classes.contactIconContainer}>
              <PhoneNumber />
              <div className={classes.title}>
                <Link
                  className={classes.link}
                  href={"tel:" + contacts.phone}
                  underline="none"
                >
                  <OverflowTooltip text={contacts.phone} />
                </Link>
              </div>
            </div>
            <div className={classes.contactIconContainer}>
              <Email />
              <div className={classes.title}>
                <Link
                  className={classes.link}
                  href={"mailto:" + contacts.email}
                  underline="none"
                >
                  <OverflowTooltip text={contacts.email} />
                </Link>
              </div>
            </div>
            <div className={classes.contactIconContainer}>
              <Website />
              <div className={classes.title}>
                <Link
                  className={classes.link}
                  href={urlNormalizer(contacts.website)}
                  target="_blank"
                  underline="none"
                >
                  <OverflowTooltip text={contacts.website} />
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
